import { useAtomValue, useSetAtom } from "jotai";
import { Field, Text, TextInput } from "@zapier/design-system";
import { Button } from "@zapier/design-system-beta";
import { ChangeEvent, useCallback, useState } from "react";
import {
  canvasAtom,
  createNotificationSetter,
  sessionAtom,
} from "@zapier/canvas-state";

import { useEmitUserInteractionEvents } from "../../../hooks/useEmitUserInteractionEvents";
import { deleteCanvas, DeleteCanvasErrorType } from "../../../api";
import useIsOwnerOrSuperAdmin from "../../../hooks/useIsOwnerOrSuperAdmin";

import styles from "./DeleteModalContent.module.css";

type Props = {
  onClosed: () => void;
};

function DeleteModalContent({ onClosed }: Props) {
  const createNotification = useSetAtom(createNotificationSetter);
  const canvas = useAtomValue(canvasAtom);

  const session = useAtomValue(sessionAtom);

  const [isDeleting, setIsDeleting] = useState(false);

  const [typedCanvasName, setTypedCanvasName] = useState("");

  const isAccountOwnerOrSuperAdmin = useIsOwnerOrSuperAdmin();

  const canDelete =
    isAccountOwnerOrSuperAdmin || session?.id === canvas.customuser_id;

  const { emitEventOnDeleteCanvas } = useEmitUserInteractionEvents();

  const onDeleteCanvas = useCallback(async () => {
    const canvasName = canvas.name;
    setIsDeleting(true);

    try {
      const res = await deleteCanvas(canvas.id);

      if (res.error) {
        throw res.error; // This throws the error object to be caught by the catch block
      }

      emitEventOnDeleteCanvas();

      // Success handling
      createNotification({
        type: "success",
        message: canvasName
          ? `Your "${canvasName}" canvas has been deleted.`
          : "Your canvas has been deleted.",
      });

      /* Triggers a full page reload (don't use router.push or else we will have
         a bug where you can't scroll on canvas list page) */
      window.location.href = "/app/canvas";
    } catch (error) {
      const apiError = error as DeleteCanvasErrorType;

      let errorMessage = canvasName
        ? `We ran into an issue trying to delete your "${canvasName}" canvas. Please try again.`
        : "We ran into an issue trying to delete your canvas. Please try again.";

      if (apiError.json?.reason === "PROJECTS_API_ERROR") {
        errorMessage = `We couldn't delete your Canvas because we ran into an issue trying to delete it from the project.`;
      }

      createNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsDeleting(false);
      onClosed();
    }
  }, [
    canvas.name,
    canvas.id,
    emitEventOnDeleteCanvas,
    createNotification,
    onClosed,
  ]);

  const handleDeletionInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTypedCanvasName(e.target.value);
  };

  const shouldDeleteButtonBeDisabled = () => {
    const canvasName = canvas.name;
    const typedText = typedCanvasName.trim().toLowerCase();

    if (canvasName) {
      return canvasName.toLowerCase() !== typedText;
    } else {
      return typedText !== "delete";
    }
  };

  return (
    <div className={styles.modalContentWrapper}>
      <div className={styles.deleteWrapper}>
        <div className={styles.deleteInputWrapper}>
          <Field
            label="Canvas name"
            isDisabled={!canDelete}
            renderHelpText={() => (
              <Text type="SmallPrint1" color="GrayWarm9">
                {canvas.name
                  ? `Type the name of this canvas and click "Delete" to confirm.`
                  : `Type "Delete" then click to confirm.`}
              </Text>
            )}
            renderInput={(inputProps) => (
              <TextInput
                {...inputProps}
                value={typedCanvasName}
                disabledText={
                  canDelete ? "" : "You don't have permission to delete"
                }
                placeholder={canvas.name ? canvas.name : `Delete`}
                onChange={handleDeletionInputChange}
              />
            )}
          />
        </div>
        <div className={styles.deleteButtonWrapper}>
          <Button
            variant="danger"
            onPress={onDeleteCanvas}
            isLoading={isDeleting}
            isDisabled={shouldDeleteButtonBeDisabled()}
          >
            Delete
          </Button>
        </div>
      </div>
      <div>
        <Button variant="secondary" onPress={onClosed}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default DeleteModalContent;
